import { html, rune, View } from 'rune-ts';
import klass from './PopupStoreProgress.module.scss';
import { staticTypo } from '../../../../shared/typography/typo';
import { classes, htmlIf } from '../../../../shared/util';
import { UtilTimeF } from '../../../../../../modules/Util/Time/F/Function/module/UtilTimeF';
import { UtilS } from '../../../../../../modules/Util/S/Function/module/UtilS';
import { endOfMinute, format } from 'date-fns';
import type { PopupStoreListType } from '../../../../features/PopupStores/type';

export interface PopupStoreProgressProps {
  tab?: PopupStoreListType;
  is_soldout: boolean;
  soldout_label: string;
  start_at: string;
  end_at: string;
  visible: string;
  size: 'small' | 'large';
}

export class Timer extends View<{ end_at }> {
  status: {
    days: string;
    hours: string;
    minutes: string;
    seconds: string;
  };

  constructor(props) {
    super(props);
    const end_time = endOfMinute(new Date(props.end_at));
    const now = Date.now();
    this.status = this.calcRemainTime(now, end_time);
  }

  override template() {
    const { days, hours, minutes, seconds } = this.status;
    const is_mobile = rune.getSharedData(this)?.is_mobile;
    return html`
      <div class="${klass.timer}">
        <p>${days}${is_mobile ? 'D' : 'DAY'}</p>
        <p>${hours}:${minutes}:${seconds}</p>
      </div>
    `;
  }

  calcRemainTime(now, end_time) {
    if (!end_time) return { days: '0', hours: '00', minutes: '00', seconds: '00' };
    const { day, hour, minute, second } = UtilS.differenceTimeInUnits(
      ['day', 'hour', 'minute', 'second'],
      end_time,
      now,
    );
    const pad = (num) => (num + '').padStart(2, '0');
    return {
      days: day,
      hours: pad(Math.max(0, hour)),
      minutes: pad(Math.max(0, minute)),
      seconds: pad(Math.max(0, second)),
    };
  }

  runClock(end_at) {
    const end_time = endOfMinute(new Date(end_at));
    let now = Date.now();
    UtilTimeF.setExactSecondInterval(() => {
      const { days, hours, minutes, seconds } = this.calcRemainTime(new Date(now), end_time);
      now += 1000;
      this.status.days = days;
      this.status.hours = hours;
      this.status.minutes = minutes;
      this.status.seconds = seconds;
      this.redraw();
    }, 10);
  }

  override onRender() {
    this.runClock(this.data.end_at);
  }
}

export class PopupStoreProgress extends View<PopupStoreProgressProps> {
  average(val, base) {
    return Math.round((val / base) * 100);
  }

  makeGoalHtml(type, current) {
    return html`
      <div class="${klass.current}">
        ${htmlIf(html`<p>KRW</p>`, type === 'price')}
        <p>${current.toLocaleString()}</p>
        ${htmlIf(html`<p>SOLD</p>`, type === 'count')}
      </div>
    `;
  }

  makeRemainHtml(start, end) {
    return html`
      <div class="${klass.remain_date} ${staticTypo('unica_20_bold')}">
        <p>${format(new Date(start), 'yyyy-MM-dd HH:mm')}</p>
        ~
        <p>${format(new Date(end), 'yyyy-MM-dd HH:mm')}</p>
      </div>
    `;
  }

  override template() {
    const { tab, is_soldout, soldout_label, size, visible, start_at, end_at } = this.data;
    const is_mobile = rune.getSharedData(this)?.is_mobile;
    const value_font = classes({
      [staticTypo('bebas_96_bold')]: !is_mobile && size === 'large',
      [staticTypo('bebas_48_bold')]: is_mobile && size === 'large',
      [staticTypo('unica_24_bold')]: size === 'small',
    });

    return html`
      <div
        class="${klass.popup_store_progress} ${classes({
          [klass.large]: size === 'large',
          [klass.small]: size === 'small',
        })}"
      >
        <div class="${klass.values} ${value_font}">
          ${htmlIf(
            new Timer({ end_at: tab === 'now' ? end_at : start_at }),
            !is_soldout && visible === 'timer',
          )}
          ${htmlIf(html`<p>${html.preventEscape(soldout_label)}</p>`, is_soldout)}
        </div>
      </div>
    `;
  }
}
